import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Offcanvas navigation for the header block world
 * (`snippets/jgd-nav-drawer.liquid`).
 *
 * Contents are cloned at runtime from the mega/extra block markup in the
 * same header (single source of truth, no duplicated drawer payload):
 * - one tab per `_jgd-mega-menu` axis (tab bar hidden with a single axis;
 *   swipeable with fade edges when the axes don't fit the head),
 * - level 1 of the active axis as list; deeper levels slide in as layers
 *   ("Zurück zu X" / "Alles aus X" per layer),
 * - flat `_jgd-nav-links` groups from the main container in the footer
 *   area, groups from `jgd-nav-extra` at the very bottom.
 *
 * Level 2/3 may still be pending hydration (data-jgd-pending on the
 * flyout container) — a loading layer is shown and filled once the
 * hydration morph delivers the content.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLElement} drawer
 * @property {HTMLElement} tabs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} root
 *
 * @extends {Component<Refs>}
 */
class JgdNavDrawer extends Component {
  requiredRefs = ['details', 'drawer', 'tabs', 'viewport', 'root'];

  /** @type {HTMLElement[]} */
  #layers = [];

  /** @type {Element | null} */
  #caretTemplate = null;

  /** @type {ResizeObserver | null} */
  #tabsObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.tabs.addEventListener('scroll', this.#updateTabFades, { passive: true });
    // Also fires once the closed <details> lays the tab bar out on open.
    this.#tabsObserver = new ResizeObserver(this.#updateTabFades);
    this.#tabsObserver.observe(this.refs.tabs);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.tabs.removeEventListener('scroll', this.#updateTabFades);
    this.#tabsObserver?.disconnect();
    this.#tabsObserver = null;
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Summary click (declarative) or programmatic toggle. The native
   * summary toggle is prevented — the component manages the `open`
   * attribute itself, so programmatic opens (e.g. the "More" slot of
   * the mega bar) behave identically: the details toggle event drives
   * scroll-lock (dialog.js) and the containment release (CSS).
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault?.();
    return this.isOpen ? this.close() : this.open();
  }

  open() {
    // Rebuild from the live DOM on every open: the nav markup can have
    // been re-rendered in the meantime (hydration morph, section
    // re-renders) — cached clones would then point at detached nodes
    // and layers would come up empty.
    this.#rebuild();

    this.refs.details.open = true;

    const summary = this.refs.details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      this.refs.details.classList.add('menu-open');
      setTimeout(() => trapFocus(this.refs.details), 300);
    });
  }

  close() {
    const summary = this.refs.details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    this.refs.details.classList.remove('menu-open');

    setTimeout(() => {
      this.refs.details.removeAttribute('open');
      this.#resetLayers();
      removeTrapFocus();
    }, 300);
  }

  back() {
    this.#popLayer();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    if (this.#layers.length > 0) {
      this.#popLayer();
    } else {
      this.close();
    }
  };

  /* ===== Content build ===== */

  #rebuild() {
    this.#resetLayers();
    this.refs.root.replaceChildren();
    this.refs.tabs.replaceChildren();
    this.refs.tabs.scrollLeft = 0;
    this.refs.tabs.hidden = true;
    this.#build();
  }

  #build() {
    const header = this.closest('header-component');
    if (!header) return;

    this.#caretTemplate = header.querySelector('.jgd-mega__rail-link .icon-caret') ?? null;

    const axes = /** @type {HTMLElement[]} */ ([...header.querySelectorAll('jgd-mega-nav .jgd-mega__item')]);

    /** @type {HTMLElement[]} */
    const panes = [];

    for (const [index, axis] of axes.entries()) {
      const pane = document.createElement('div');
      pane.className = 'jgd-nav-drawer__pane';
      pane.setAttribute('role', 'tabpanel');
      pane.id = `jgd-nav-drawer-pane-${index}`;
      if (index > 0) pane.hidden = true;

      const list = document.createElement('ul');
      list.className = 'jgd-nav-drawer__list';
      list.setAttribute('role', 'list');

      for (const railItem of axis.querySelectorAll('.jgd-mega__rail-item')) {
        const railLink = railItem.querySelector('.jgd-mega__rail-link');
        if (!(railLink instanceof HTMLAnchorElement)) continue;

        const li = document.createElement('li');
        const flyoutIndex = railLink.getAttribute('data-jgd-flyout-for');

        if (flyoutIndex != null) {
          li.appendChild(
            this.#buildPushEntry(this.#titleOf(railLink), () => this.#pushLevel2Layer(axis, railLink, flyoutIndex))
          );
        } else {
          li.appendChild(this.#buildLinkEntry(railLink));
        }

        list.appendChild(li);
      }

      pane.appendChild(list);
      panes.push(pane);
      this.refs.root.appendChild(pane);
    }

    this.#buildTabs(axes, panes);
    this.#buildFlatGroups(header);
  }

  /**
   * @param {HTMLElement[]} axes
   * @param {HTMLElement[]} panes
   */
  #buildTabs(axes, panes) {
    if (axes.length < 2) return;

    this.refs.tabs.hidden = false;

    for (const [index, axis] of axes.entries()) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'jgd-nav-drawer__tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', `${index === 0}`);
      tab.setAttribute('aria-controls', `jgd-nav-drawer-pane-${index}`);
      tab.setAttribute('data-track', 'nav_drawer_tab');
      tab.textContent = axis.getAttribute('data-jgd-label') ?? '';

      tab.addEventListener('click', () => {
        this.#resetLayers();
        for (const [paneIndex, pane] of panes.entries()) {
          pane.hidden = paneIndex !== index;
        }
        for (const other of this.refs.tabs.querySelectorAll('[role="tab"]')) {
          other.setAttribute('aria-selected', `${other === tab}`);
        }
        this.#revealTab(tab);
      });

      this.refs.tabs.appendChild(tab);
    }
  }

  /**
   * Scrolls a partly hidden tab fully into the bar, clear of the fade edge.
   * Manual scrollBy instead of scrollIntoView — the latter would also
   * scroll the clipped panel ancestors on iOS.
   * @param {HTMLElement} tab
   */
  #revealTab(tab) {
    const bar = this.refs.tabs;
    const barRect = bar.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const fade = parseFloat(getComputedStyle(bar).getPropertyValue('--jgd-tabs-fade')) || 0;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

    if (tabRect.left < barRect.left + fade) {
      bar.scrollBy({ left: tabRect.left - barRect.left - fade, behavior });
    } else if (tabRect.right > barRect.right - fade) {
      bar.scrollBy({ left: tabRect.right - barRect.right + fade, behavior });
    }
  }

  #updateTabFades = () => {
    const bar = this.refs.tabs;
    const max = bar.scrollWidth - bar.clientWidth;
    bar.toggleAttribute('data-fade-start', max > 1 && bar.scrollLeft > 1);
    bar.toggleAttribute('data-fade-end', max > 1 && bar.scrollLeft < max - 1);
  };

  /**
   * Flat link groups: main container groups in the footer area, groups
   * from "Weitere Links" (adopted items are marked data-jgd-extra) last.
   * @param {Element} header
   */
  #buildFlatGroups(header) {
    const items = /** @type {HTMLElement[]} */ ([...header.querySelectorAll('.jgd-nav-links__item')]);
    if (items.length === 0) return;

    const footer = document.createElement('div');
    footer.className = 'jgd-nav-drawer__footer';

    const sorted = [...items.filter((item) => !item.hasAttribute('data-jgd-extra'))].concat(
      items.filter((item) => item.hasAttribute('data-jgd-extra'))
    );

    /** @type {string | null} */
    let currentGroup = null;
    /** @type {HTMLUListElement | null} */
    let list = null;

    for (const item of sorted) {
      const group = item.getAttribute('data-jgd-group');
      const link = item.querySelector('a');
      if (!(link instanceof HTMLAnchorElement)) continue;

      if (group !== currentGroup || !list) {
        currentGroup = group;

        const label = header.querySelector(`.jgd-nav-links__item[data-jgd-group="${group}"][data-jgd-group-label]`);
        const labelText = label?.getAttribute('data-jgd-group-label');
        if (labelText) {
          const heading = document.createElement('p');
          heading.className = 'jgd-nav-drawer__group-label';
          heading.textContent = labelText;
          footer.appendChild(heading);
        }

        list = document.createElement('ul');
        list.className = 'jgd-nav-drawer__list';
        list.setAttribute('role', 'list');
        footer.appendChild(list);
      }

      const li = document.createElement('li');
      li.appendChild(this.#buildLinkEntry(link));
      list.appendChild(li);
    }

    this.refs.root.appendChild(footer);
  }

  /* ===== Layers ===== */

  /**
   * @param {HTMLElement} axis
   * @param {HTMLAnchorElement} railLink
   * @param {string} flyoutIndex
   */
  #pushLevel2Layer(axis, railLink, flyoutIndex) {
    const axisLabel = axis.getAttribute('data-jgd-label') ?? '';
    const axisHandle = axis.getAttribute('data-jgd-axis');
    const layer = this.#pushLayer(this.#titleOf(railLink), railLink.href, axisLabel, railLink);
    const body = /** @type {HTMLElement} */ (layer.querySelector('[data-jgd-layer-body]'));

    // The captured node can go stale when the nav markup is re-rendered
    // (hydration morph, section re-render) — always resolve against the
    // live DOM before reading from it.
    const liveAxis = () =>
      axis.isConnected
        ? axis
        : /** @type {HTMLElement | null} */ (
            document.querySelector(`jgd-mega-nav .jgd-mega__item[data-jgd-axis="${axisHandle}"]`)
          ) ?? axis;

    const fill = () => {
      const flyout = liveAxis().querySelector(`.jgd-mega__flyout[data-jgd-flyout="${flyoutIndex}"]`);
      if (!flyout) {
        body.replaceChildren(this.#buildLoading());
        return;
      }

      const list = document.createElement('ul');
      list.className = 'jgd-nav-drawer__list';
      list.setAttribute('role', 'list');

      for (const group of flyout.querySelectorAll('.jgd-mega__group')) {
        const groupLink = group.querySelector('.jgd-mega__group-link');
        if (!(groupLink instanceof HTMLAnchorElement)) continue;

        const li = document.createElement('li');
        const childList = group.querySelector('.jgd-mega__group-list');

        if (childList) {
          li.appendChild(
            this.#buildPushEntry(this.#titleOf(groupLink), () =>
              this.#pushLevel3Layer(groupLink, childList, this.#titleOf(railLink))
            )
          );
        } else {
          li.appendChild(this.#buildLinkEntry(groupLink));
        }

        list.appendChild(li);
      }

      body.replaceChildren(list);
    };

    const flyouts = liveAxis().querySelector('.jgd-mega__flyouts');
    if (flyouts?.hasAttribute('data-jgd-pending')) {
      body.replaceChildren(this.#buildLoading());
      const observer = new MutationObserver(() => {
        if (flyouts.isConnected && flyouts.hasAttribute('data-jgd-pending')) return;
        observer.disconnect();
        fill();
      });
      observer.observe(flyouts, { childList: true, attributes: true });
    } else {
      fill();
    }
  }

  /**
   * @param {HTMLAnchorElement} groupLink
   * @param {Element} childList
   * @param {string} parentTitle
   */
  #pushLevel3Layer(groupLink, childList, parentTitle) {
    const layer = this.#pushLayer(this.#titleOf(groupLink), groupLink.href, parentTitle, groupLink);
    const body = /** @type {HTMLElement} */ (layer.querySelector('[data-jgd-layer-body]'));

    const list = document.createElement('ul');
    list.className = 'jgd-nav-drawer__list';
    list.setAttribute('role', 'list');

    for (const childLink of childList.querySelectorAll('a')) {
      const li = document.createElement('li');
      li.appendChild(this.#buildLinkEntry(childLink));
      list.appendChild(li);
    }

    body.replaceChildren(list);
  }

  /**
   * Builds and animates in a layer with back button and "Alles aus X".
   * @param {string} title
   * @param {string} url
   * @param {string} parentTitle
   * @param {HTMLAnchorElement} sourceLink - Menu link the layer belongs to ("Alles aus X" shares its target)
   * @returns {HTMLElement}
   */
  #pushLayer(title, url, parentTitle, sourceLink) {
    const layer = document.createElement('div');
    layer.className = 'jgd-nav-drawer__layer';

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'jgd-nav-drawer__back';
    back.appendChild(this.#buildCaret());
    back.appendChild(
      document.createTextNode((this.dataset.textBack ?? 'Zurück zu [title]').replace('[title]', parentTitle))
    );
    back.addEventListener('click', () => this.#popLayer());
    layer.appendChild(back);

    const body = document.createElement('div');
    body.setAttribute('data-jgd-layer-body', '');

    if (url && !url.endsWith('#')) {
      const viewAll = document.createElement('a');
      viewAll.href = url;
      viewAll.className = 'jgd-nav-drawer__item jgd-nav-drawer__view-all';
      viewAll.textContent = (this.dataset.textViewAll ?? 'Alles aus [title]').replace('[title]', title);
      const trackNav = sourceLink.getAttribute('data-track-nav');
      if (trackNav != null) viewAll.setAttribute('data-track-nav', trackNav);
      layer.appendChild(viewAll);
    }

    layer.appendChild(body);
    this.refs.viewport.appendChild(layer);
    this.#layers.push(layer);

    requestAnimationFrame(() => {
      layer.classList.add('is-open');
      // preventScroll + Reset: iOS Safari scrollt beim Fokussieren sonst
      // den Viewport horizontal zum noch nicht eingeschobenen Layer —
      // Ebene 0 rutscht aus dem Bild, der Drawer wirkt leer.
      back.focus({ preventScroll: true });
      this.#resetViewportScroll();
      setTimeout(() => this.#resetViewportScroll(), 350);
    });

    return layer;
  }

  #resetViewportScroll() {
    this.refs.viewport.scrollLeft = 0;
    this.refs.viewport.scrollTop = 0;
  }

  #popLayer() {
    const layer = this.#layers.pop();
    if (!layer) return;

    layer.classList.remove('is-open');
    setTimeout(() => layer.remove(), 300);

    const focusTarget = this.#layers[this.#layers.length - 1] ?? this.refs.root;
    const focusable = focusTarget.querySelector('button, a');
    if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
    this.#resetViewportScroll();
  }

  #resetLayers() {
    for (const layer of this.#layers) layer.remove();
    this.#layers = [];
  }

  /* ===== Entry builders ===== */

  /**
   * @param {HTMLAnchorElement} sourceLink
   * @returns {HTMLAnchorElement}
   */
  #buildLinkEntry(sourceLink) {
    const link = document.createElement('a');
    link.href = sourceLink.href;
    link.className = 'jgd-nav-drawer__item';
    link.textContent = this.#titleOf(sourceLink);

    for (const attribute of ['data-jgd-axis', 'data-jgd-level', 'data-jgd-target', 'data-track-nav']) {
      const value = sourceLink.getAttribute(attribute);
      if (value != null) link.setAttribute(attribute, value);
    }

    if (sourceLink.getAttribute('aria-current')) link.setAttribute('aria-current', 'page');

    return link;
  }

  /**
   * @param {string} title
   * @param {() => void} onPush
   * @returns {HTMLButtonElement}
   */
  #buildPushEntry(title, onPush) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'jgd-nav-drawer__item';

    const text = document.createElement('span');
    text.textContent = title;
    button.appendChild(text);
    button.appendChild(this.#buildCaret());

    button.addEventListener('click', onPush);

    return button;
  }

  /**
   * @returns {Element}
   */
  #buildCaret() {
    if (this.#caretTemplate) {
      const caret = this.#caretTemplate.cloneNode(true);
      if (caret instanceof Element) {
        caret.setAttribute('aria-hidden', 'true');
        return caret;
      }
    }

    const fallback = document.createElement('span');
    fallback.className = 'svg-wrapper icon-caret';
    fallback.setAttribute('aria-hidden', 'true');
    fallback.textContent = '›';
    return fallback;
  }

  #buildLoading() {
    const wrapper = document.createElement('div');
    wrapper.className = 'jgd-nav-drawer__loading';
    wrapper.setAttribute('role', 'status');

    const spinner = document.createElement('span');
    spinner.className = 'jgd-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(spinner);

    const text = document.createElement('span');
    text.textContent = this.dataset.textLoading ?? '';
    wrapper.appendChild(text);

    return wrapper;
  }

  /**
   * @param {Element} link
   * @returns {string}
   */
  #titleOf(link) {
    const title = link.querySelector('.jgd-mega__rail-title');
    return (title ?? link).textContent?.trim() ?? '';
  }
}

if (!customElements.get('jgd-nav-drawer')) {
  customElements.define('jgd-nav-drawer', JgdNavDrawer);
}
